// src/services/affiliation/affiliationService.js
//
// Web port of lib/services/affiliation/affiliation_service.dart
//
// NOTE: Adjust the import below to match wherever your project initializes
// Firestore/Auth (e.g. src/services/firebaseConfig.js, src/firebase.js, etc).
// It just needs to export `db` (a Firestore instance) and `auth` (an Auth instance).
import { auth, db } from '../../firebase';

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';

// ── Status / type constants ─────────────────────────────────────────────────
// Mirrors AffiliationStatus / SellerCategoryType from the Flutter models.

export const AffiliationStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
};

// IMPORTANT: these must match the exact strings written to
// `users/{uid}.verification.categoryType` by the verification flow
// (see CategorySelectionPage.jsx / category_selection_page.dart), not an
// arbitrary lowercase convention — otherwise every equality query against
// `verification.categoryType` silently returns zero matches, and
// `myCategoryType === SellerCategoryType.personal` in AffiliationScreen
// never resolves true either.
export const SellerCategoryType = {
  personal: 'Personal',
  business: 'Business&Organizations',
};

const AffiliationNotificationType = {
  request: 'affiliation_request',
  approved: 'affiliation_approved',
  rejected: 'affiliation_rejected',
};

// ── Collection helpers ──────────────────────────────────────────────────────

const affiliationsCol = () => collection(db, 'affiliations');
const notificationsCol = () => collection(db, 'notifications');
const usersCol = () => collection(db, 'users');

const currentUid = () => auth.currentUser?.uid ?? null;

// Simple RFC4122-ish id using the browser's native crypto API
// (avoids pulling in the `uuid` package just for this).
const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `aff_${Date.now()}_${Math.random().toString(36).slice(2)}`;

// ── Search ───────────────────────────────────────────────────────────────
//
// Searches verified accounts of `targetType` whose username matches `query`
// (prefix match preferred, falls back to "contains").
//
// Deliberately avoids a Firestore range filter on `username` combined with
// equality filters — that combination needs a composite index and is
// case-sensitive. Instead this fetches candidates using only equality
// filters (categoryType + status, servable without a custom index) and
// matches the username client-side, case-insensitively — same approach as
// the Flutter version.
async function searchAccounts({ query: rawQuery, targetType }) {
  const trimmed = (rawQuery || '').trim().toLowerCase();
  if (!trimmed) return [];

  const myUid = currentUid();

  try {
    const q = query(
      usersCol(),
      where('verification.categoryType', '==', targetType),
      where('verification.status', '==', 'approved'),
      limit(200)
    );
    const snap = await getDocs(q);

    const candidates = snap.docs
      .filter((d) => d.id !== myUid)
      .map((d) => ({ uid: d.id, ...d.data() }));

    const prefixMatches = [];
    const containsMatches = [];

    for (const user of candidates) {
      const username = (user.username || '').toLowerCase();
      if (!username) continue;
      if (username.startsWith(trimmed)) {
        prefixMatches.push(user);
      } else if (username.includes(trimmed)) {
        containsMatches.push(user);
      }
    }

    return [...prefixMatches, ...containsMatches].slice(0, 10);
  } catch (error) {
    console.error('affiliationService.searchAccounts error:', error);
    return [];
  }
}

// ── Sending a proposal ──────────────────────────────────────────────────
//
// Returns true if the request was created, false if it was blocked
// (already affiliated / already pending / invalid target).
async function sendAffiliationRequest({ targetUser }) {
  const me = auth.currentUser;
  if (!me) return false;

  const targetUid = targetUser?.uid;
  if (!targetUid || targetUid === me.uid) return false;

  // Avoid duplicate pending/approved requests between the same pair.
  const existingSnap = await getDocs(
    query(affiliationsCol(), where('participants', 'array-contains', me.uid))
  );
  const alreadyLinked = existingSnap.docs.some((d) => {
    const data = d.data();
    const participants = data.participants || [];
    const status = data.status;
    return (
      participants.includes(targetUid) &&
      (status === AffiliationStatus.pending || status === AffiliationStatus.approved)
    );
  });
  if (alreadyLinked) return false;

  const myDocSnap = await getDoc(doc(db, 'users', me.uid));
  const myData = myDocSnap.data() || {};
  const myVerification = myData.verification || {};
  const targetVerification = targetUser.verification || {};

  const affiliationId = newId();

  const data = {
    affiliationId,
    participants: [me.uid, targetUid],
    requesterUid: me.uid,
    requesterName: myData.displayName || myData.name || '',
    requesterUsername: myData.username || '',
    requesterType: myVerification.categoryType ?? null,
    targetUid,
    targetName: targetUser.displayName || targetUser.name || '',
    targetUsername: targetUser.username || '',
    targetType: targetVerification.categoryType ?? null,
    status: AffiliationStatus.pending,
    createdAt: serverTimestamp(),
    respondedAt: null,
  };

  await setDoc(doc(affiliationsCol(), affiliationId), data);

  await addDoc(notificationsCol(), {
    userId: targetUid,
    type: AffiliationNotificationType.request,
    title: 'New affiliation request',
    body: `@${data.requesterUsername} wants to affiliate with your channel`,
    fromUid: me.uid,
    fromName: data.requesterName,
    fromUsername: data.requesterUsername,
    relatedId: affiliationId,
    isRead: false,
    createdAt: serverTimestamp(),
  });

  return true;
}

async function withdrawAffiliationRequest(affiliationId) {
  const me = currentUid();
  if (!me) return;
  const ref = doc(affiliationsCol(), affiliationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();
  if (data.requesterUid !== me) return;
  if (data.status !== AffiliationStatus.pending) return;
  await deleteDoc(ref);
}

// ── Responding to a proposal ────────────────────────────────────────────

async function respondToAffiliationRequest({ affiliationId, approve }) {
  const me = currentUid();
  if (!me) return;

  const ref = doc(affiliationsCol(), affiliationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();
  if (data.targetUid !== me) return;

  const newStatus = approve ? AffiliationStatus.approved : AffiliationStatus.rejected;

  await updateDoc(ref, {
    status: newStatus,
    respondedAt: serverTimestamp(),
  });

  const { requesterUid, targetName, targetUsername } = data;

  await addDoc(notificationsCol(), {
    userId: requesterUid,
    type: approve ? AffiliationNotificationType.approved : AffiliationNotificationType.rejected,
    title: approve ? 'Affiliation approved' : 'Affiliation declined',
    body: approve
      ? `@${targetUsername} approved your affiliation request`
      : `@${targetUsername} declined your affiliation request`,
    fromUid: me,
    fromName: targetName,
    fromUsername: targetUsername,
    relatedId: affiliationId,
    isRead: false,
    createdAt: serverTimestamp(),
  });
}

// ── Live subscriptions ───────────────────────────────────────────────────
//
// Web has no Dart Streams, so each of these takes a callback and returns an
// unsubscribe function — the same convention already used by
// notificationService.unreadCountStream() in SettingsScreen.jsx.

/** Pending requests received by me — need my approve/reject action. */
function streamIncomingRequests(callback) {
  const me = currentUid();
  if (!me) {
    callback([]);
    return () => {};
  }
  const q = query(
    affiliationsCol(),
    where('targetUid', '==', me),
    where('status', '==', AffiliationStatus.pending),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

/** Pending requests I sent out — awaiting the other side's decision. */
function streamProposedByMe(callback) {
  const me = currentUid();
  if (!me) {
    callback([]);
    return () => {};
  }
  const q = query(
    affiliationsCol(),
    where('requesterUid', '==', me),
    where('status', '==', AffiliationStatus.pending),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

/** Confirmed affiliations involving me, in either direction. */
function streamAffiliates(callback) {
  const me = currentUid();
  if (!me) {
    callback([]);
    return () => {};
  }
  const q = query(
    affiliationsCol(),
    where('participants', 'array-contains', me),
    where('status', '==', AffiliationStatus.approved),
    orderBy('respondedAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

/** Live count of pending incoming requests — used for the settings badge. */
function incomingRequestCountStream(callback) {
  return streamIncomingRequests((docs) => callback(docs.length));
}

export const affiliationService = {
  searchAccounts,
  sendAffiliationRequest,
  withdrawAffiliationRequest,
  respondToAffiliationRequest,
  streamIncomingRequests,
  streamProposedByMe,
  streamAffiliates,
  incomingRequestCountStream,
};

export default affiliationService;